/**
 * src/shell/agents/services/GenUISynthesizer.ts
 *
 * Natural-Language to Component Specification (DSL) Synthesizer.
 * Translates conversational prompts into structured 12-column grid layouts
 * with RBAC capability checks against `RoleEnforcementEngine`.
 */

import {
  ICanvasDashboardSpec,
  IGenUIWidgetSpec,
  WidgetType,
} from '../types/genui-types';
import { roleEnforcement, UserRole } from '../../../main/security/RoleEnforcement';

export class GenUISynthesizer {
  private static instance: GenUISynthesizer;

  private constructor() {}

  public static getInstance(): GenUISynthesizer {
    if (!GenUISynthesizer.instance) {
      GenUISynthesizer.instance = new GenUISynthesizer();
    }
    return GenUISynthesizer.instance;
  }

  public synthesizeDashboard(
    prompt: string,
    userContext?: { userId: string; role: UserRole }
  ): ICanvasDashboardSpec {
    const lower = prompt.toLowerCase();
    const role: UserRole = userContext?.role || 'ordinary_user';
    const widgets: IGenUIWidgetSpec[] = [];

    let currentY = 0;

    // 1. Check for KPI / Overview metrics
    if (lower.includes('overview') || lower.includes('kpi') || lower.includes('metric') || lower.includes('access') || lower.includes('dashboard')) {
      widgets.push({
        id: `widget_kpi_${Date.now()}_1`,
        type: 'kpi_card',
        title: 'Active Nodes & Relay Latency',
        description: 'Cluster connectivity telemetry',
        layout: { id: 'kpi_1', x: 0, y: currentY, w: 4, h: 2 },
        dataSource: { endpoint: 'telemetry:nodes', refreshIntervalMs: 2000 },
        colorScheme: 'emerald',
        config: { metric: 'node_count', trend: '+2 this hour', unit: 'peers' },
      });

      widgets.push({
        id: `widget_kpi_${Date.now()}_2`,
        type: 'kpi_card',
        title: 'Door Access Assertions',
        description: 'Real-time BLE unlocks',
        layout: { id: 'kpi_2', x: 4, y: currentY, w: 4, h: 2 },
        dataSource: { endpoint: 'access:doors', refreshIntervalMs: 1000 },
        colorScheme: 'default',
        config: { metric: 'unlock_count', trend: '12 today', unit: 'events' },
      });

      widgets.push({
        id: `widget_kpi_${Date.now()}_3`,
        type: 'kpi_card',
        title: 'microCredit Velocity',
        description: 'Spend and allocation velocity',
        layout: { id: 'kpi_3', x: 8, y: currentY, w: 4, h: 2 },
        dataSource: { endpoint: 'ledger:credits', refreshIntervalMs: 3000 },
        colorScheme: 'cyber',
        config: { metric: 'credit_velocity', trend: '14.2/min', unit: 'µCredits' },
      });

      currentY += 2;
    }

    // 2. Check for Heatmap requests
    if (lower.includes('heat') || lower.includes('density') || lower.includes('access') || lower.includes('room')) {
      // Ordinary user is allowed only personal or scoped access
      widgets.push({
        id: `widget_heat_${Date.now()}`,
        type: 'access_heatmap',
        title: 'Door Access Density Matrix',
        description: 'Hourly entry and security events across assigned perimeter doors',
        layout: { id: 'heat_1', x: 0, y: currentY, w: 6, h: 4 },
        dataSource: { endpoint: 'access:doors', refreshIntervalMs: 5000 },
        colorScheme: lower.includes('red') || lower.includes('crimson') ? 'crimson' : 'default',
        config: { highlightAnomalies: true },
      });
    }

    // 3. Check for Credit / Financial flow requests
    if (lower.includes('credit') || lower.includes('finance') || lower.includes('flow') || lower.includes('sankey') || lower.includes('spend')) {
      widgets.push({
        id: `widget_sankey_${Date.now()}`,
        type: 'credit_sankey',
        title: 'microCredit Flow & Resource Allocation',
        description: 'Interactive distribution of compute bandwidth vs reward yields',
        layout: { id: 'sankey_1', x: 6, y: currentY, w: 6, h: 4 },
        dataSource: { endpoint: 'ledger:credits', refreshIntervalMs: 4000 },
        colorScheme: 'emerald',
        config: { showFees: true },
      });
      currentY += 4;
    }

    // 4. Check for Topology / Cluster maps
    if (lower.includes('topology') || lower.includes('mesh') || lower.includes('network') || lower.includes('cluster')) {
      // Gated for mesh_owner and superadmin
      const canViewTopology = role === 'mesh_owner' || role === 'superadmin';
      if (canViewTopology) {
        widgets.push({
          id: `widget_topology_${Date.now()}`,
          type: 'mesh_topology',
          title: 'P2P Gossip Mesh Topology & Peer Graph',
          description: 'Live WebRTC DataChannel connections and route latencies',
          layout: { id: 'topo_1', x: 0, y: currentY, w: 7, h: 4 },
          dataSource: { endpoint: 'telemetry:nodes', refreshIntervalMs: 2000 },
          colorScheme: 'cyber',
          config: { renderGlow: true },
        });
      }
    }

    // 5. Check for Access Timeline
    if (lower.includes('timeline') || lower.includes('log') || lower.includes('audit') || lower.includes('security')) {
      widgets.push({
        id: `widget_timeline_${Date.now()}`,
        type: 'access_timeline',
        title: 'Real-Time Access & Event Stream',
        description: 'Chronological feed of biometric unlocks and security assertions',
        layout: { id: 'timeline_1', x: widgets.some((w) => w.type === 'mesh_topology') ? 7 : 0, y: currentY, w: 5, h: 4 },
        dataSource: { endpoint: 'access:doors', refreshIntervalMs: 1500 },
        colorScheme: 'amber',
        config: { maxItems: 10 },
      });
      currentY += 4;
    }

    // Default fallback if no specific keywords matched
    if (widgets.length === 0) {
      widgets.push({
        id: `widget_kpi_${Date.now()}`,
        type: 'kpi_card',
        title: 'Cluster Status Overview',
        description: 'Standard compute and security state',
        layout: { id: 'kpi_def', x: 0, y: 0, w: 6, h: 2 },
        dataSource: { endpoint: 'telemetry:nodes' },
        colorScheme: 'default',
        config: { metric: 'healthy', unit: 'nodes' },
      });

      widgets.push({
        id: `widget_timeline_${Date.now()}`,
        type: 'access_timeline',
        title: 'Live Event Stream',
        layout: { id: 'time_def', x: 6, y: 0, w: 6, h: 4 },
        dataSource: { endpoint: 'access:doors' },
        colorScheme: 'amber',
        config: { maxItems: 8 },
      });
    }

    return {
      id: `dash_${Date.now()}`,
      title: this.deriveTitle(prompt),
      generatedFromPrompt: prompt,
      widgets,
      version: 1,
      lastMutatedAt: new Date().toISOString(),
    };
  }

  private deriveTitle(prompt: string): string {
    const trimmed = prompt.trim();
    if (trimmed.length < 30) {
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    }
    return `Dashboard: ${trimmed.slice(0, 30)}…`;
  }
}

export const genUISynthesizer = GenUISynthesizer.getInstance();
